import { Tooltip } from "@mui/material";
import React, { useRef, useEffect, useState } from "react";

interface TruncatedTooltipProps {
  title: string;
  children: React.ReactElement;
  placement?: 
    | "bottom-end"
    | "bottom-start" 
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
}

/**
 * A tooltip component that only shows when the content is truncated with ellipsis.
 * Automatically responds to window resize and element size changes.
 */
const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({ 
  title, 
  children, 
  placement = "top-start" 
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const element = textRef.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  };

  useEffect(() => {
    checkTruncation();
  }, [title]);

  useEffect(() => {
    const handleResize = () => {
      checkTruncation();
    };

    window.addEventListener('resize', handleResize);
    
    const resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });

    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Tooltip 
      title={isTruncated ? title : ""} 
      placement={placement} 
      disableHoverListener={!isTruncated}
    >
      {React.cloneElement(children, { ref: textRef })}
    </Tooltip>
  );
};

export default TruncatedTooltip;
